import {
  Button,
  HeroControlPanel,
  HeroControlSection,
  HeroControlSwitch,
} from '@wundo/ui';
import { Mic } from 'lucide-react';
import { m } from '@/paraglide/messages.js';

/**
 * What the fysiek-gesprek section has to say. `null` is the organisation that
 * does not have microphone recording, and also the moment one is running — the
 * red pill beside this panel says everything there is to say then.
 */
export type MeetingOfflineOffer = 'idle' | 'processing' | 'done' | 'failed' | null;

export interface MeetingNotesControlProps {
  /** Whether the notetaker joins this conversation. */
  on: boolean;
  /** Whether this meeting has an online link the notetaker could join. */
  hasOnlineLink: boolean;
  /**
   * A meeting the user wrote down themselves. There is no notetaker to switch
   * on — nothing was ever invited — so the panel is only the fysiek section.
   */
  manual: boolean;
  /** Microphone recording of a physical meeting — see the type. */
  offline: MeetingOfflineOffer;
  /** Whether this browser can record at all. */
  canRecord: boolean;
  /** While a correction or a start is in flight, so nothing fires twice. */
  pending: boolean;
  /** Asked for a flip; what it costs is settled by the scope question after it. */
  onFlip: (next: boolean) => void;
  onStartRecording: () => void;
}

/**
 * What Wundo does at this conversation, in the row under the hero.
 *
 * The pill says the state it is in — "Notetaker aan", "Notetaker uit" — and the
 * panel behind it is where that state is explained and changed. Two levels on
 * purpose: the answer to "does Wundo sit in on this one?" is worth reading at a
 * glance, and the sentence that says what sitting in means is not.
 *
 * On a meeting with no online link a notetaker that is on has nowhere to go, so
 * nothing will be recorded. The switch shows that — off, and not flippable,
 * because a flip would change nothing about this meeting — and the description
 * says why instead of claiming Wundo listens in. The stored choice is untouched
 * and keeps applying to meetings that do have a link. A notetaker that is off
 * stays an ordinary off switch: turning it on is a standing choice that matters
 * beyond this one linkless meeting.
 *
 * Under it, where the organisation has microphone recording, the other way of
 * getting a verslag: the meeting that happens in a room. It is a section rather
 * than a second switch because it is not a setting — recording starts now, or
 * it does not start.
 *
 * The panel is named after the conversation rather than after the switch,
 * because this is where anything else Wundo may be told to do here would land.
 */
export function MeetingNotesControl({
  on,
  hasOnlineLink,
  manual,
  offline,
  canRecord,
  pending,
  onFlip,
  onStartRecording,
}: MeetingNotesControlProps) {
  const idle = on && !hasOnlineLink;

  return (
    <HeroControlPanel
      icon={<Mic aria-hidden='true' />}
      value={manual ? offlineValue(offline) : notetakerValue(on, idle)}
      title={m.meeting_wundo_here()}
    >
      {!manual && (
        <HeroControlSwitch
          label={m.meeting_notes_switch_label()}
          description={
            idle ? m.meeting_notes_no_link_body() : m.meeting_notes_switch_body()
          }
          checked={on && hasOnlineLink}
          disabled={pending || idle}
          onCheckedChange={onFlip}
        />
      )}

      {offline !== null && (
        <HeroControlSection title={m.meeting_offline_section()}>
          <p className='text-md text-ink-muted'>{offlineBody(offline, canRecord)}</p>
          {/*
            A finished recording is offered again, because stopping too early
            is the ordinary mistake here. What that costs is not said on the
            button but in the dialog behind it, where the answer is still
            somebody's to take back.
          */}
          {(offline === 'idle' || offline === 'done') && canRecord && (
            <Button
              className='mt-2.5'
              size='sm'
              disabled={pending}
              onClick={onStartRecording}
            >
              {m.meeting_offline_start()}
            </Button>
          )}
        </HeroControlSection>
      )}
    </HeroControlPanel>
  );
}

/** What the pill says on a meeting whose only offer is the microphone. */
function offlineValue(offline: MeetingOfflineOffer): string {
  if (offline === 'processing') return m.meeting_offline_processing_pill();
  if (offline === 'done') return m.meeting_offline_done_pill();
  if (offline === 'failed') return m.meeting_offline_failed_pill();
  return m.meeting_offline_pill_idle();
}

/** And on a meeting the notetaker could sit in on. */
function notetakerValue(on: boolean, idle: boolean): string {
  if (idle) return m.meeting_notes_no_link();
  return on ? m.meeting_notes_on() : m.meeting_notes_off();
}

/**
 * The sentence under the section heading. A recording that is being read back
 * and one that could not be read are both states, not offers — neither gets a
 * button, and the failed one is not retried in front of the user, because
 * transcription failing twice is a thing to be told about rather than clicked
 * at. A finished one says it is finished and still offers a new recording,
 * which replaces it.
 */
function offlineBody(
  offline: Exclude<MeetingOfflineOffer, null>,
  canRecord: boolean,
): string {
  if (offline === 'processing') return m.meeting_offline_processing_body();
  if (offline === 'done') return m.meeting_offline_done_body();
  if (offline === 'failed') return m.meeting_offline_failed_body();
  return canRecord ? m.meeting_offline_body() : m.meeting_offline_unsupported();
}
